import { boundedText } from "./bounded-text.js";
import { contactChannelPreferences, } from "./concierge-assignment.js";
import { DomainValidationError } from "./identifiers.js";
// The customer's own profile and notification preferences (CHT-739, Milestone C lane D2; contract sections 4.10 and 8.2).
//
// Only three profile fields are editable by the customer: the preferred name, the locale and the contact channel preference. The
// full name, the email, the phone number, the home market and the avatar are never changed through this path. The database
// CHECKs repeat these rules (a client with the column grants writes directly), so the two stay in step.
//
// Version policy (one rule for both aggregates): a read returns `version`. An edit carries it back as `expectedVersion`. The write
// applies only while the stored version is still that number and moves it by exactly one; otherwise nothing is written and the
// answer is a conflict. The client reloads and the customer decides again. An edit is never merged and never overwrites a newer state.
export const PROFILE_PREFERRED_NAME_MAX = 100;
/** The same pattern as the CHECK `customer_profiles_locale_format`: `fr`, `fr-FR`, `zh-Hant-TW`, `es-419`. */
export const PROFILE_LOCALE_PATTERN = /^[a-z]{2,3}(-[A-Z][a-z]{3})?(-([A-Z]{2}|[0-9]{3}))?$/u;
export const PROFILE_LOCALE_MAX = 35;
export const profileEditableFields = Object.freeze([
    "preferredName",
    "locale",
    "contactChannelPreference",
]);
/**
 * Validates an edit and returns only the fields it names, normalized (a name is trimmed). A bad value throws
 * `DomainValidationError` with the field and the rule, never the value. An edit with no field at all is refused.
 */
export function validateProfileEdit(input) {
    const edit = {};
    if (input.preferredName !== undefined) {
        edit.preferredName =
            input.preferredName === null
                ? null
                : boundedText("preferredName", input.preferredName, PROFILE_PREFERRED_NAME_MAX);
    }
    if (input.locale !== undefined) {
        if (input.locale === null) {
            edit.locale = null;
        }
        else if (typeof input.locale !== "string" ||
            input.locale.length > PROFILE_LOCALE_MAX ||
            !PROFILE_LOCALE_PATTERN.test(input.locale)) {
            throw new DomainValidationError("locale", "must be a language tag such as fr-FR");
        }
        else {
            edit.locale = input.locale;
        }
    }
    if (input.contactChannelPreference !== undefined) {
        if (input.contactChannelPreference !== null &&
            !contactChannelPreferences.includes(input.contactChannelPreference)) {
            throw new DomainValidationError("contactChannelPreference", "is not a known channel");
        }
        edit.contactChannelPreference = input.contactChannelPreference;
    }
    if (Object.keys(edit).length === 0) {
        throw new DomainValidationError("profile", "needs at least one field");
    }
    return Object.freeze(edit);
}
/** The fields that an edit changes against the current profile (codes, never values: the audit row lists these). */
export function changedProfileFields(current, edit) {
    return profileEditableFields.filter((field) => edit[field] !== undefined && edit[field] !== current[field]);
}
/**
 * The optional notification topics. A service message (a concierge reply, a payment due, a booking confirmation) is
 * transactional: it has no topic and no toggle, and a customer cannot switch it off here.
 */
export const notificationTopics = Object.freeze(["TRIP_INSPIRATION", "PRODUCT_NEWS"]);
/** A customer with no saved row reads as every topic off, at version 1. The first save inserts version 2. */
export const DEFAULT_NOTIFICATION_TOPICS = Object.freeze({
    TRIP_INSPIRATION: false,
    PRODUCT_NEWS: false,
});
export const NOTIFICATION_PREFERENCES_FIRST_VERSION = 1;
/** The topics that differ between two sets, as codes. */
export function changedNotificationTopics(current, next) {
    return notificationTopics.filter((topic) => current[topic] !== next[topic]);
}
/** Every topic must be a boolean: a missing or a non-boolean topic is refused (no default is guessed). */
export function validateNotificationTopics(input) {
    if (typeof input !== "object" || input === null) {
        throw new DomainValidationError("topics", "must be an object");
    }
    const record = input;
    const topics = {};
    for (const topic of notificationTopics) {
        const value = record[topic];
        if (typeof value !== "boolean") {
            throw new DomainValidationError("topics", "must give every topic a true or false");
        }
        topics[topic] = value;
    }
    return Object.freeze(topics);
}
